import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { surveyAPI } from '../../utils/api';
import Alert from '../common/Alert';

const SurveyList = ({ currentUser }) => {
  const [surveys, setSurveys] = useState([]);
  const [managers, setManagers] = useState([]);
  const [selectedManager, setSelectedManager] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [alert, setAlert] = useState({ show: false, message: '', type: 'info' });
  const navigate = useNavigate();
  const basePath = currentUser?.role === 'admin' ? '/admin' : '/users';

  useEffect(() => {
    loadSurveys(currentPage, selectedManager);
  }, [currentPage, selectedManager]);

  useEffect(() => {
    if (currentUser?.role === 'admin') {
      loadManagers();
    }
  }, []);

  const loadSurveys = async (page = 1, managerId = 'all') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: page.toString() });
      if (managerId !== 'all') {
        params.append('manager_id', managerId);
      }
      
      const response = await fetch(`/api/surveys?${params}`, {
        credentials: 'same-origin'
      });
      
      if (response.ok) {
        const data = await response.json();
        setSurveys(data.surveys || []);
        setPagination(data.pagination);
        setError(null);
      } else {
        throw new Error('Failed to load surveys');
      }
    } catch (err) {
      setError('Failed to load surveys');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const loadManagers = async () => {
    try {
      const response = await fetch('/api/managers?unpaginated=true', {
        credentials: 'same-origin'
      });
      if (response.ok) {
        const data = await response.json();
        setManagers(data);
      }
    } catch (err) {
      console.error('Failed to load managers:', err);
    }
  };

  const handleManagerFilter = (managerId) => {
    setSelectedManager(managerId);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id, title) => {
    if (!confirm(`Are you sure you want to delete "${title}"?`)) return;
    
    try {
      await surveyAPI.delete(id);
      setAlert({ show: true, message: 'Survey deleted successfully', type: 'success' });
      loadSurveys(currentPage, selectedManager);
    } catch (err) {
      setAlert({ show: true, message: 'Failed to delete survey', type: 'error' });
      console.error(err);
    }
  };

  const togglePublish = async (survey) => {
    try {
      await surveyAPI.update(survey.id, { survey: { published: !survey.published } });
      loadSurveys(currentPage, selectedManager);
    } catch (err) {
      setAlert({ show: true, message: 'Failed to update survey', type: 'error' });
      console.error(err);
    }
  };

  const handleDuplicate = async (surveyId) => {
    try {
      const response = await surveyAPI.getOne(surveyId);
      const originalSurvey = response.data;
      
      const duplicatedData = {
        title: `Copy of ${originalSurvey.title}`,
        description: originalSurvey.description,
        published: false,
        manager_id: '',
        questions: originalSurvey.questions.map(q => ({
          text: q.text,
          category: q.category
        }))
      };
      
      const duplicateToken = `duplicate_${Date.now()}`;
      sessionStorage.setItem(duplicateToken, JSON.stringify(duplicatedData));
      sessionStorage.setItem('latestSurveyDuplicateToken', duplicateToken);
      
      navigate(`${basePath}/surveys/new`, { 
        state: { 
          duplicateData: duplicatedData,
          duplicateToken: duplicateToken
        } 
      });
    } catch (err) {
      setAlert({ show: true, message: 'Failed to duplicate survey', type: 'error' });
      console.error(err);
    }
  };

  const styles = {
    container: {
      backgroundColor: 'white',
      borderRadius: '8px',
      padding: '2rem',
      boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '2rem'
    },
    createBtn: {
      padding: '0.75rem 1.5rem',
      backgroundColor: '#07638e',
      color: 'white',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      textDecoration: 'none',
      display: 'inline-block'
    },
    table: {
      width: '100%',
      borderCollapse: 'collapse'
    },
    th: {
      textAlign: 'left',
      padding: '1rem',
      borderBottom: '2px solid #dee2e6',
      backgroundColor: '#f8f9fa'
    },
    td: {
      padding: '1rem',
      borderBottom: '1px solid #dee2e6'
    },
    badge: {
      padding: '0.25rem 0.75rem',
      borderRadius: '12px',
      fontSize: '0.875rem',
      fontWeight: 'bold'
    },
    published: {
      backgroundColor: '#28a745',
      color: 'white'
    },
    draft: {
      backgroundColor: '#ffc107',
      color: '#000'
    },
    actionBtn: {
      padding: '0.5rem 0.75rem',
      margin: '0 0.25rem',
      border: 'none',
      borderRadius: '4px',
      cursor: 'pointer',
      fontSize: '0.875rem'
    },
    editBtn: {
      backgroundColor: '#07638e',
      color: 'white'
    },
    deleteBtn: {
      backgroundColor: '#6c757d',
      color: 'white'
    },
    viewBtn: {
      backgroundColor: '#07638e',
      color: 'white'
    },
    reportBtn: {
      backgroundColor: '#07638e',
      color: 'white'
    },
    toggleBtn: {
      backgroundColor: '#6c757d',
      color: 'white'
    },
    duplicateBtn: {
      backgroundColor: '#07638e',
      color: 'white'
    },
    emptyState: {
      textAlign: 'center',
      padding: '3rem',
      color: '#6c757d'
    },
    filterContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '1rem',
      marginBottom: '1.5rem',
      padding: '1rem',
      backgroundColor: '#f8f9fa',
      borderRadius: '6px'
    },
    filterLabel: {
      fontWeight: 600,
      color: '#495057'
    },
    filterSelect: {
      padding: '0.5rem 1rem',
      borderRadius: '4px',
      border: '1px solid #ced4da',
      fontSize: '1rem',
      cursor: 'pointer'
    },
    paginationContainer: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      gap: '1rem',
      marginTop: '2rem',
      padding: '1rem'
    },
    pageButton: {
      padding: '0.5rem 1rem',
      border: '1px solid #dee2e6',
      borderRadius: '4px',
      backgroundColor: 'white',
      cursor: 'pointer',
      fontSize: '0.875rem'
    },
    pageButtonActive: {
      backgroundColor: '#07638e',
      color: 'white',
      borderColor: '#07638e'
    },
    pageButtonDisabled: {
      opacity: 0.5,
      cursor: 'not-allowed'
    },
    pageInfo: {
      fontSize: '0.875rem',
      color: '#6c757d'
    }
  };

  const dropdownItem = {
    width: "100%",
    padding: "10px 12px",
    textAlign: "left",
    border: "none",
    background: "white",
    cursor: "pointer",
    fontSize: "14px",
    borderBottom: "1px solid #eee",
  };

  dropdownItem[':last-child'] = { borderBottom: 'none' };


  if (loading) return <div style={styles.container}>Loading surveys...</div>;
  if (error) return <div style={styles.container}>Error: {error}</div>;

  return (
    <div style={styles.container}>
      <Alert 
        show={alert.show}
        message={alert.message}
        type={alert.type}
        onClose={() => setAlert({ ...alert, show: false })}
      />
      <div style={styles.header}>
        <h2>Survey Management</h2>
        {currentUser?.role === 'admin' && (
          <Link to={`${basePath}/surveys/new`} style={styles.createBtn}>
            Create New Survey
          </Link>
        )}
      </div>

      {currentUser?.role === 'admin' && managers.length > 0 && (
        <div style={styles.filterContainer}>
          <span style={styles.filterLabel}>Filter by Manager:</span>
          <select 
            style={styles.filterSelect}
            value={selectedManager}
            onChange={(e) => handleManagerFilter(e.target.value)}
          >
            <option value="all">All Managers</option>
            {managers.map(manager => (
              <option key={manager.id} value={manager.id}>
                {manager.full_name} ({manager.department})
              </option>
            ))}
          </select>
          {selectedManager !== 'all' && pagination && (
            <span style={{fontSize: '0.875rem', color: '#6c757d'}}>
              Showing {pagination.total_count} survey(s)
            </span>
          )}
        </div>
      )}

      {surveys.length === 0 ? (
        <div style={styles.emptyState}>
          <h3>No surveys found</h3>
          <p>Create your first survey to get started!</p>
        </div>
      ) : (
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Title</th>
              <th style={styles.th}>Description</th>
              <th style={styles.th}>Manager</th>
              <th style={styles.th}>Questions</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {surveys.map(survey => (
              <tr key={survey.id}>
                <td style={styles.td}><strong>{survey.title}</strong></td>
                <td style={styles.td}>{survey.description?.substring(0, 80)}...</td>
                <td style={styles.td}>{survey.manager.full_name}</td>
                <td style={styles.td}>{survey.questions_count || 0}</td>
                <td style={styles.td}>
                  {currentUser?.role === 'admin' ? (
                    <span style={{...styles.badge, ...(survey.published ? styles.published : styles.draft)}}>
                      {survey.published ? 'Published' : 'Draft'}
                    </span>
                  ) : (
                    <span style={{
                      ...styles.badge,
                      backgroundColor: survey.status === 'completed' ? '#28a745' : survey.status === 'in_progress' ? '#ffc107' : '#6c757d',
                      color: survey.status === 'in_progress' ? '#000' : 'white'
                    }}>
                      {survey.status?.replace('_', ' ').toUpperCase()} {survey.progress !== undefined && `(${survey.progress}%)`}
                    </span>
                  )}
                </td>
                <td style={styles.td}>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <button
                      onClick={() => setOpenDropdown(openDropdown === survey.id ? null : survey.id)}
                      style={{
                        padding: "6px 12px",
                        borderRadius: "6px",
                        border: "1px solid #ccc",
                        background: "#F7F7F7",
                        cursor: "pointer",
                      }}
                    >
                      ⋮
                    </button>

                    {openDropdown === survey.id && (
                      <div
                        style={{
                          position: "absolute",
                          top: "40px",
                          right: 0,
                          background: "#fff",
                          border: "1px solid #ddd",
                          borderRadius: "8px",
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                          width: "180px",
                          zIndex: 10,
                        }}
                      >
                        {currentUser?.role !== 'admin' && (
                          <button
                            onClick={() => navigate(`${basePath}/surveys/${survey.id}`)}
                            style={dropdownItem}
                          >
                            {survey.status === 'completed' ? 'View Responses' : 'Take Survey'}
                          </button>
                        )}

                        {currentUser?.role === 'admin' && (
                          <>
                            <button
                              onClick={() => navigate(`${basePath}/surveys/${survey.id}`)}
                              style={dropdownItem}
                            >
                              View
                            </button>

                            <button
                              onClick={() => navigate(`${basePath}/surveys/${survey.id}/edit`)}
                              style={dropdownItem}
                            >
                              Edit
                            </button>

                            <button
                              onClick={() => handleDuplicate(survey.id)}
                              style={dropdownItem}
                            >
                              Duplicate
                            </button>

                            <button
                              onClick={() => navigate(`${basePath}/surveys/${survey.id}/stats`)}
                              style={dropdownItem}
                            >
                              View Stats
                            </button>

                            <button
                              onClick={() => togglePublish(survey)}
                              style={dropdownItem}
                            >
                              {survey.published ? 'Unpublish' : 'Publish'}
                            </button>

                            <button
                              onClick={() => handleDelete(survey.id, survey.title)}
                              style={{ ...dropdownItem, color: "red" }}
                            >
                              Delete
                            </button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pagination && pagination.total_pages > 1 && (
        <div style={styles.paginationContainer}>
          <button
            onClick={() => handlePageChange(currentPage - 1)}
            disabled={currentPage === 1}
            style={{
              ...styles.pageButton,
              ...(currentPage === 1 ? styles.pageButtonDisabled : {})
            }}
          >
            ← Previous
          </button>
          
          <div style={{display: 'flex', gap: '0.5rem'}}>
            {[...Array(pagination.total_pages)].map((_, index) => {
              const page = index + 1;
              if (
                page === 1 ||
                page === pagination.total_pages ||
                (page >= currentPage - 1 && page <= currentPage + 1)
              ) {
                return (
                  <button
                    key={page}
                    onClick={() => handlePageChange(page)}
                    style={{
                      ...styles.pageButton,
                      ...(page === currentPage ? styles.pageButtonActive : {})
                    }}
                  >
                    {page}
                  </button>
                );
              } else if (
                page === currentPage - 2 ||
                page === currentPage + 2
              ) {
                return <span key={page} style={{padding: '0.5rem'}}>...</span>;
              }
              return null;
            })}
          </div>

          <button
            onClick={() => handlePageChange(currentPage + 1)}
            disabled={currentPage === pagination.total_pages}
            style={{
              ...styles.pageButton,
              ...(currentPage === pagination.total_pages ? styles.pageButtonDisabled : {})
            }}
          >
            Next →
          </button>

          <span style={styles.pageInfo}>
            Page {currentPage} of {pagination.total_pages} ({pagination.total_count} total)
          </span>
        </div>
      )}
    </div>
  );
};

export default SurveyList;
